"use client";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import { navigation } from "@/Helpers/WorkspaceData";
import { Icon } from "../icons";
import { Button } from "../ui";
import { useWorkspace } from "../workspace";
import type { WorkspacePanel } from "./types";

export function Header({
  user,
  onOpenMenu,
  onOpenPanel,
}: {
  user: { name: string; initials: string };
  onOpenMenu: () => void;
  onOpenPanel: (panel: WorkspacePanel) => void;
}) {
  const { locale, t, clients } = useWorkspace();
  const pathname = usePathname();
  const [search, setSearch] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const listener = (event: KeyboardEvent) => {
      if (
        event.key === "/" &&
        !["INPUT", "TEXTAREA", "SELECT"].includes(
          (event.target as HTMLElement).tagName,
        )
      ) {
        event.preventDefault();
        searchRef.current?.focus();
      }
      if (event.key === "Escape") setSearch("");
    };
    document.addEventListener("keydown", listener);
    return () => document.removeEventListener("keydown", listener);
  }, []);
  useEffect(() => {
    setSearch("");
  }, [pathname]);
  const term = search.trim().toLocaleLowerCase(locale);
  const results = [
    ...navigation
      .filter((n) => t(n.label).toLocaleLowerCase(locale).includes(term))
      .map((n) => ({
        label: t(n.label),
        href: `/${locale}/${n.screen}`,
        icon: n.icon,
        detail: "",
      })),
    ...clients
      .filter((c) =>
        `${c.name} ${c.fullName} ${c.id}`
          .toLocaleLowerCase(locale)
          .includes(term),
      )
      .map((c) => ({
        label: c.name,
        href: `/${locale}/clients/${c.id}`,
        icon: "users",
        detail: c.id,
      })),
  ].slice(0, 7);

  function switchLocaleHref(next: string) {
    return pathname.replace(/^\/(nl|en)(?=\/|$)/, `/${next}`);
  }
  return (
    <header className="topbar h-19 border-b border-b-line bg-[#ffffffed] flex items-center gap-5 py-0 px-7.5 sticky top-0 z-35 backdrop-blur-[10px] min-[1600px]:pl-9.5 min-[1600px]:pr-9.5 max-[1200px]:py-0 max-[1200px]:px-5.5 max-[1200px]:gap-3.75 max-[1000px]:gap-3 max-[1000px]:h-17 max-[760px]:py-0 max-[760px]:px-4 max-[760px]:h-16 max-[760px]:gap-2.5">
      <button
        className="icon-button mobile-toggle border border-transparent bg-transparent inline-flex items-center justify-center w-8.5 h-8.5 rounded-lg text-muted flex-none relative [&:hover]:bg-line-soft [&:hover]:text-ink [&.mobile-toggle]:hidden max-[760px]:[&.mobile-toggle]:inline-flex"
        onClick={() => onOpenMenu()}
        aria-label={t("menu")}
      >
        <Icon name="menu" />
      </button>
      <div className="global-search flex items-center gap-2.5 max-w-[470px] flex-1 text-[#8290a4] relative [&_input]:w-full [&_input]:border-0 [&_input]:bg-transparent [&_input]:text-[12px] [&_input]:outline-none [&_input]:py-2.5 [&_input]:px-0 [&_input]:min-w-0 [&_input::placeholder]:text-[#8290a4] [&_kbd]:font-[inherit] [&_kbd]:border [&_kbd]:border-line [&_kbd]:rounded-[5px] [&_kbd]:py-0 [&_kbd]:px-1.5 [&_kbd]:text-[11px] [&_kbd]:leading-[21px] max-[1000px]:[&_input]:text-[11px] max-[1000px]:[&_kbd]:hidden max-[760px]:gap-1.75 max-[760px]:[&_input]:text-[10px] max-[760px]:[&_>_.icon]:w-3.75 max-[760px]:[&_>_.icon]:h-3.75">
        <Icon name="search" />
        <input
          ref={searchRef}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t("search")}
          aria-label={t("search")}
          aria-expanded={!!term}
          aria-controls="global-results"
          autoComplete="off"
        />
        <kbd>/</kbd>
        {term && (
          <div
            className="search-results absolute top-10.5 -left-3 -right-3 border border-line bg-white rounded-xl shadow-[0_12px_40px_#0f1b2d20] p-1.5 [&_>_a]:flex [&_>_a]:items-center [&_>_a]:gap-2.5 [&_>_a]:py-3 [&_>_a]:px-2.5 [&_>_a]:rounded-[7px] [&_>_a]:text-ink [&_>_a:hover]:bg-teal-soft [&_>_a_>_small]:ml-auto [&_>_a_>_.icon:last-child]:w-3.25 [&_>_p]:p-3.5 [&_>_p]:text-muted"
            id="global-results"
          >
            {results.length ? (
              results.map((r) => (
                <Link key={r.href} href={r.href} onClick={() => setSearch("")}>
                  <Icon name={r.icon} />
                  <span>{r.label}</span>
                  <small>{r.detail}</small>
                  <Icon name="chevron" />
                </Link>
              ))
            ) : (
              <p>{t("noResults")}</p>
            )}
          </div>
        )}
      </div>
      <div className="top-actions flex items-center gap-2.75 ml-auto max-[1200px]:gap-1.75 max-[1200px]:[&_.audit-button]:hidden max-[1000px]:[&_.optional-label]:hidden max-[1000px]:[&_.assistant-button]:text-[0] max-[1000px]:[&_.assistant-button]:gap-0 max-[1000px]:[&_.assistant-button_.icon]:w-4.25 max-[1000px]:[&_.assistant-button_.icon]:h-4.25 max-[1000px]:[&_.notification-button]:hidden max-[1000px]:gap-1.75 max-[760px]:gap-1.5 max-[760px]:[&_.btn]:py-1.75 max-[760px]:[&_.btn]:px-2.25 max-[450px]:[&_>_.btn]:hidden">
        <div
          className="locale-switch flex items-center gap-0.5 p-0.75 bg-mist border border-line rounded-[7px] [&_a]:text-[10px] [&_a]:font-semibold [&_a]:py-1 [&_a]:px-1.5 [&_a]:rounded [&_a]:text-muted [&_a.active]:bg-white [&_a.active]:text-teal [&_a.active]:shadow-[0_1px_4px_#0f1b2d10] max-[760px]:[&_a]:text-[9px] max-[760px]:[&_a]:py-1 max-[760px]:[&_a]:px-1.25 max-[760px]:p-0.5"
          aria-label={t("language")}
        >
          {(["nl", "en"] as const).map((code) => (
            <Link
              href={switchLocaleHref(code)}
              onClick={() => {
                document.cookie = `baken-locale=${code};path=/;max-age=31536000;SameSite=Lax`;
                setSearch("");
              }}
              key={code}
              lang={code}
              aria-current={locale === code ? "true" : undefined}
              className={locale === code ? "active" : ""}
            >
              {code.toUpperCase()}
            </Link>
          ))}
        </div>
        <Button icon="plus" onClick={() => onOpenPanel("new")}>
          <span className="optional-label">{t("new")}</span>
        </Button>
        <Button
          className="assistant-button"
          icon="spark"
          onClick={() => {
            onOpenPanel("assistant");
          }}
        >
          {t("assistant")}
        </Button>
        <button
          className="icon-button notification-button border border-transparent bg-transparent inline-flex items-center justify-center w-8.5 h-8.5 rounded-lg text-muted flex-none relative [&:hover]:bg-line-soft [&:hover]:text-ink [&.mobile-toggle]:hidden max-[760px]:[&.mobile-toggle]:inline-flex"
          onClick={() => onOpenPanel("notifications")}
          aria-label={t("notifications")}
        >
          <Icon name="bell" />
          <span className="notification-dot absolute right-1.75 top-1.25 w-1.25 h-1.25 bg-[#df6470] rounded-full border border-white" />
        </button>
        <button
          className="icon-button audit-button border border-transparent bg-transparent inline-flex items-center justify-center w-8.5 h-8.5 rounded-lg text-muted flex-none relative [&:hover]:bg-line-soft [&:hover]:text-ink [&.mobile-toggle]:hidden max-[760px]:[&.mobile-toggle]:inline-flex"
          onClick={() => onOpenPanel("audit")}
          aria-label={t("audit")}
        >
          <Icon name="clock" />
        </button>
        <Link
          className="avatar top-avatar w-8.5 h-8.5 rounded-[10px] inline-flex items-center justify-center flex-none bg-teal text-white text-[11px] font-semibold ml-0.25 max-[1000px]:hidden"
          href={`/${locale}/settings`}
          aria-label={user.name}
        >
          {user.initials}
        </Link>
      </div>
    </header>
  );
}
